const LOCKED_STYLE_PROPERTIES = [
  "position",
  "top",
  "left",
  "width",
  "overflow",
] as const;

type LockedStyleProperty = (typeof LOCKED_STYLE_PROPERTIES)[number];

interface SavedStyleProperty {
  value: string;
  priority: string;
}

interface ScrollLockTarget {
  style: Pick<
    CSSStyleDeclaration,
    | "getPropertyPriority"
    | "getPropertyValue"
    | "removeProperty"
    | "setProperty"
  >;
}

interface ScrollLockViewport {
  readonly scrollX: number;
  readonly scrollY: number;
  scrollTo(x: number, y: number): void;
}

interface ScrollLockSnapshot {
  x: number;
  y: number;
  styles: Record<LockedStyleProperty, SavedStyleProperty>;
}

export interface DocumentScrollLock {
  lock(): void;
  unlock(): void;
  isLocked(): boolean;
}

export function createDocumentScrollLock(
  target: ScrollLockTarget = document.body,
  viewport: ScrollLockViewport = window,
): DocumentScrollLock {
  let snapshot: ScrollLockSnapshot | null = null;

  return {
    lock(): void {
      if (snapshot) {
        return;
      }

      const styles = Object.fromEntries(
        LOCKED_STYLE_PROPERTIES.map((property) => [
          property,
          {
            value: target.style.getPropertyValue(property),
            priority: target.style.getPropertyPriority(property),
          },
        ]),
      ) as Record<LockedStyleProperty, SavedStyleProperty>;

      snapshot = {
        x: viewport.scrollX,
        y: viewport.scrollY,
        styles,
      };

      target.style.setProperty("position", "fixed");
      target.style.setProperty("top", `${-snapshot.y}px`);
      target.style.setProperty("left", `${-snapshot.x}px`);
      target.style.setProperty("width", "100%");
      target.style.setProperty("overflow", "hidden");
    },

    unlock(): void {
      if (!snapshot) {
        return;
      }

      const lockedSnapshot = snapshot;
      snapshot = null;
      for (const property of LOCKED_STYLE_PROPERTIES) {
        const saved = lockedSnapshot.styles[property];
        if (saved.value) {
          target.style.setProperty(property, saved.value, saved.priority);
        } else {
          target.style.removeProperty(property);
        }
      }
      viewport.scrollTo(lockedSnapshot.x, lockedSnapshot.y);
    },

    isLocked(): boolean {
      return snapshot !== null;
    },
  };
}
